import { useEffect } from 'react';

export type ThemeColorVar = 'spona-bg-container' | 'spona-bg-base' | 'spona-brand-primary';

/**
 * Sets the browser <meta name="theme-color"> to the resolved value of a CSS
 * custom property defined in the Spona design system. The previous theme color
 * is restored when the component that called the hook unmounts.
 *
 * @param colorVar - The CSS variable name (without `--` prefix) to use as the
 *   theme color. Defaults to `'spona-bg-container'` which matches the static
 *   fallback value set in index.html.
 */
export const usePageThemeColor = (colorVar: ThemeColorVar = 'spona-bg-container'): void => {
    useEffect(() => {
        const metaTag = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
        if (!metaTag) return;

        const resolvedColor = getComputedStyle(document.documentElement)
            .getPropertyValue(`--${colorVar}`)
            .trim();

        if (!resolvedColor) return;

        const previousColor = metaTag.getAttribute('content') ?? '';
        metaTag.setAttribute('content', resolvedColor);

        return () => {
            metaTag.setAttribute('content', previousColor);
        };
    }, [colorVar]);
};
